import React, { useEffect, useRef, useState } from 'react';
import logo from 'assets/new_icons/new_mabel_logo_login-preview.png';

const CLOSE_DELAY_SECONDS = 4;

const attemptClose = () => {
	try { window.close(); } catch (_) { /* ignored */ }
};

const OAuthCallback: React.FC = () => {
	const params = new URLSearchParams(window.location.search);
	const isSuccess = params.get('status') === 'connected';
	const displayMessage = params.get('message') || (isSuccess ? 'Connected successfully.' : 'An error occurred.');

	const [status] = useState<'success' | 'error'>(isSuccess ? 'success' : 'error');
	const [countdown, setCountdown] = useState(CLOSE_DELAY_SECONDS);
	const [closeFailed, setCloseFailed] = useState(false);
	const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		let remaining = CLOSE_DELAY_SECONDS;

		tickRef.current = setInterval(() => {
			remaining -= 1;
			setCountdown(remaining);

			if (remaining <= 0) {
				clearInterval(tickRef.current!);
				attemptClose();
				setTimeout(() => {
					attemptClose();
					setTimeout(() => {
						attemptClose();
						setTimeout(() => {
							if (!document.hidden) setCloseFailed(true);
						}, 500);
					}, 500);
				}, 300);
			}
		}, 1000);

		return () => { if (tickRef.current) clearInterval(tickRef.current); };
	}, []);

	const countdownLabel = closeFailed
		? null
		: countdown > 0
			? `Closing in ${countdown} second${countdown === 1 ? '' : 's'}…`
			: 'Closing…';

	return (
		<div style={styles.page}>
			<style>{`
				@keyframes mabel-pop {
					0%   { transform: scale(0.6); opacity: 0; }
					70%  { transform: scale(1.1); }
					100% { transform: scale(1);   opacity: 1; }
				}
			`}</style>

			{/* Logo — mirrors AuthLayout */}
			<img src={logo} alt="Mabel logo" style={styles.logo} />

			<div style={styles.card}>
				{status === 'success' && (
					<>
						<div style={styles.iconCircleSuccess}>
							<svg width="36" height="36" viewBox="0 0 36 36" fill="none">
								<path d="M7 18l8 8L29 10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
							</svg>
						</div>
						<p style={styles.title}>Authentication Successful</p>
						<p style={styles.body}>{displayMessage}</p>
						{!closeFailed && countdownLabel && (
							<p style={styles.countdown}>{countdownLabel}</p>
						)}
						{closeFailed && (
							<p style={styles.manualClose}>You may now close this tab.</p>
						)}
					</>
				)}

				{status === 'error' && (
					<>
						<div style={styles.iconCircleError}>
							<svg width="36" height="36" viewBox="0 0 36 36" fill="none">
								<path d="M11 11l14 14M25 11L11 25" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/>
							</svg>
						</div>
						<p style={styles.title}>Connection Failed</p>
						<p style={styles.body}>{displayMessage}</p>
						{!closeFailed && countdownLabel && (
							<p style={styles.countdown}>{countdownLabel}</p>
						)}
						{closeFailed && (
							<p style={styles.manualClose}>You may now close this tab.</p>
						)}
					</>
				)}
			</div>

			<p style={styles.footer}>© {new Date().getFullYear()} BYOD, Inc. All rights reserved.</p>
		</div>
	);
};

const styles: Record<string, React.CSSProperties> = {
	page: {
		minHeight: '100vh',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#446388',
		fontFamily: '"Gotham", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
		padding: '24px',
		gap: '28px',
	},
	logo: {
		width: '180px',
		objectFit: 'contain',
	},
	card: {
		backgroundColor: '#ffffff',
		borderRadius: '10px',
		padding: '40px 36px',
		width: '100%',
		maxWidth: '420px',
		textAlign: 'center',
		boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
	},
	iconCircleSuccess: {
		width: '72px',
		height: '72px',
		borderRadius: '50%',
		backgroundColor: '#3c721d',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		margin: '0 auto 20px',
		animation: 'mabel-pop 0.35s ease-out both',
	},
	iconCircleError: {
		width: '72px',
		height: '72px',
		borderRadius: '50%',
		backgroundColor: '#9e170e',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		margin: '0 auto 20px',
		animation: 'mabel-pop 0.35s ease-out both',
	},
	title: {
		margin: '0 0 10px',
		fontSize: '18px',
		fontWeight: 600,
		color: '#1c1b1c',
	},
	body: {
		margin: '0 0 16px',
		fontSize: '14px',
		color: '#555',
		lineHeight: 1.5,
	},
	countdown: {
		margin: 0,
		fontSize: '13px',
		color: '#979797',
	},
	manualClose: {
		margin: 0,
		fontSize: '13px',
		color: '#446388',
		fontWeight: 500,
	},
	footer: {
		color: 'rgba(255,255,255,0.65)',
		fontSize: '12px',
		margin: 0,
	},
};

export default OAuthCallback;
